"use client"

import * as React from "react"

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useActiveBranchSlug } from "@/features/auth"
import { useUserBranchAccess } from "@/features/auth/hooks/use-user-branch-access"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"

import { financeApi } from "@/features/finance/services/finance-api"
import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"
import { RATIO_DISPLAY_CURRENCY } from "@/features/finance/overview/lib/overview-format"
import { Currency } from "@ibada/shared-types/finance"
import { AccountingAccountClassification, NormalBalance } from "@/features/finance/model/accounting-reports-types"
import type {
  ChartAccount,
  AccountingChartQuery,
  AccountingJournalQuery,
  AccountingLedgerQuery,
  AccountingPeriodQuery,
  CreateAccountingAccountInput,
} from "@/features/finance/model/accounting-reports-types"
import { AccountingAccountType } from "@/features/finance/model/accounting-reports-types"
import {
  type CreateFinanceAccountInput,
  type CreateFinanceActivityInput,
  type ReallocateFundsInput,
  type CreateFinanceFundInput,
  type CreateFinanceSetupFundInput,
  type UpdateFinanceFundInput,
  type FinanceFundRecord,
  type CreateFinanceTransactionInput,
  type CreateTransferInput,
  type BudgetLineMatchQuery,
  type CreateParishExpenseRequest,
  type CreateParishIncomeRequest,
  type FinanceActivitiesListQuery,
  FinanceCategoryScope,
  FinanceAccountType,
  FinanceDirectoryStatusFilter,
  FinanceFundKind,
  FinanceFundStatus,
  type FinanceAccountRecord,
  type ListFinanceAccountsQuery,
  type ListFinanceFundsQuery,
  type FinanceCurrentIncomeBalanceQuery,
  type FinanceCurrentIncomeBalancesQuery,
  type FinanceOverviewQuery,
  type ListParishTransactionsQuery,
  type FinanceTransactionListQuery,
  type ReceiptConfig,
  type UpdateFinanceAccountInput,
  type UpdateFinanceActivityInput,
  type CancelFinanceTransactionInput,
  type UpdateFinanceTransactionMetadataInput,
  type ApproveFinanceTransferRequestInput,
  type CreateFinanceTransferRequestInput,
  type CreateSavedTransferCalculationInput,
  type DeriveTransferAmountQuery,
  type FinanceTransferRequestListQuery,
} from "@/features/finance/model/finance-types"

// Budget actuals are computed from ACTIVE ledger transactions but live under a separate query root.
// We invalidate the literal key (rather than importing the budgeting feature's keys) to keep finance
// decoupled. budgetQueryKeys.all === ["budgets"]; this covers budget detail, overview, and the
// per-line transaction lists (all rooted there).
const BUDGETS_ROOT_KEY = ["budgets"] as const
// The owner setup checklist ("Create a financial account", "Add an income category") reads a separate
// dashboard query. We invalidate it by its literal key so creating an account/category refreshes the
// checklist immediately, without importing the dashboard feature's keys (mirrors workspaceSetupProgressKeys.all).
const WORKSPACE_SETUP_PROGRESS_KEY = ["dashboard", "workspace-setup-progress"] as const

type FinanceQueryClient = ReturnType<typeof useQueryClient>
type DirectoryPage<T> = { items: T[]; total: number; page: number; limit: number }
type OptimisticSnapshot = Array<[readonly unknown[], unknown]>

const optimisticId = () => `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`

async function optimisticallyPatchLists<T>(
  queryClient: FinanceQueryClient,
  queryKey: readonly unknown[],
  update: (items: T[], key: readonly unknown[]) => T[],
): Promise<OptimisticSnapshot> {
  await queryClient.cancelQueries({ queryKey })
  const snapshots = queryClient.getQueriesData<DirectoryPage<T>>({ queryKey })
  for (const [key, page] of snapshots) {
    if (!page) continue
    const items = update(page.items, key)
    queryClient.setQueryData<DirectoryPage<T>>(key, {
      ...page,
      items,
      total: Math.max(0, page.total + items.length - page.items.length),
    })
  }
  return snapshots
}

function rollbackOptimisticLists(queryClient: FinanceQueryClient, snapshots?: OptimisticSnapshot) {
  for (const [key, value] of snapshots ?? []) queryClient.setQueryData(key, value)
}

function replaceOptimisticRow<T extends { id: string }>(
  queryClient: FinanceQueryClient,
  queryKey: readonly unknown[],
  optimisticRowId: string,
  record: T,
) {
  queryClient.setQueriesData<DirectoryPage<T>>({ queryKey }, (page) =>
    page ? { ...page, items: page.items.map((item) => (item.id === optimisticRowId ? record : item)) } : page,
  )
}

/** Invalidate a set of finance sub-trees (ADR-0007: invalidate what the write actually changed). */
function invalidateFinanceKeys(queryClient: FinanceQueryClient, keys: ReadonlyArray<readonly unknown[]>) {
  for (const queryKey of keys) {
    queryClient.invalidateQueries({ queryKey })
  }
}

/**
 * A change to the finance DIRECTORY (an account, a fund, a category, an activity, a receipt template).
 *
 * Directory rows are pickers and labels across transactions, balances and the overview, so those go with them.
 * Three more that are NOT obvious, and were each a stale-UI bug when this replaced the coarse `["finance"]` root:
 *
 * - `setup()` — the module's UNLOCK GATE. `financialAccountsCount` decides whether the setup guard, the nav
 *   and the Record button consider finance configured. Without this, a treasurer creates the first account
 *   and the wizard still says finance is not set up.
 * - `accounting()` + `openingBalances()` — creating or updating an account with an opening balance posts a real
 *   `JournalEntry` (accounts.service -> OpeningBalancesService.setAccountOpening), so the journal, ledger, trial
 *   balance and statements move. The opening-balances state is built from accounts + funds, so every account and
 *   fund write changes it too.
 *
 * The fiscal calendar and reconciliation sessions genuinely do not move when a category is renamed; they are
 * left alone (and have their own scoped helpers in finance-close-trust-query.ts).
 */
export function invalidateFinanceDirectory(queryClient: FinanceQueryClient) {
  invalidateFinanceKeys(queryClient, [
    financeQueryKeys.accounts(),
    financeQueryKeys.funds(),
    financeQueryKeys.categories(),
    financeQueryKeys.activities(),
    financeQueryKeys.transactions(),
    financeQueryKeys.balances(),
    financeQueryKeys.overview(),
    financeQueryKeys.receiptConfigs(),
    financeQueryKeys.setup(),
    financeQueryKeys.onboarding(),
    financeQueryKeys.accounting(),
    financeQueryKeys.openingBalances(),
    /*
     * The onboarding allocation step reads its TOTAL from here, and that total is the sum of the accounts'
     * opening balances — so creating an account, correcting the balance on one, or closing a fund all move it.
     * Left out, the step kept showing the figure from before the edit and a treasurer could divide the old
     * total exactly, press Complete, and be refused against the new one.
     */
    financeQueryKeys.openingFundAllocations(),
    // Directory writes ARE audited — accounts.service records AccountCreated/Updated/Archived and
    // funds.service FundCreated/Updated/Closed/Reallocated, all of which land in the audit list.
    financeQueryKeys.audit(),
    WORKSPACE_SETUP_PROGRESS_KEY,
  ])
}

/**
 * Everything a POSTED ledger fact can move: the transaction lists and detail, the balances and fund
 * availability it changed, the overview/health projections derived from them, the accounting reports
 * (journal, ledger, trial balance, statements) — AND budget actuals, which live under a separate root.
 *
 * Use this for every transaction create/cancel mutation so an open budget page refetches and shows
 * updated actuals instead of stale zeros.
 */
export function invalidateTransactionDependents(queryClient: FinanceQueryClient) {
  invalidateFinanceKeys(queryClient, [
    financeQueryKeys.transactions(),
    financeQueryKeys.accounts(),
    financeQueryKeys.funds(),
    financeQueryKeys.balances(),
    financeQueryKeys.overview(),
    financeQueryKeys.accounting(),
    // A posted or cancelled fact changes what is still unmatched on an open reconciliation session, and it is
    // a recorded event in both the audit trail and the investigation event feed.
    financeQueryKeys.reconciliation(),
    financeQueryKeys.audit(),
    financeQueryKeys.investigation(),
    // The setup status counts POSTED ENTRIES and carries `isLocked`; both move on every post.
    financeQueryKeys.setup(),
    BUDGETS_ROOT_KEY,
  ])
}

/**
 * A cross-context transfer changed state. What refetches depends on WHETHER IT POSTED:
 *
 * - submit / reject / cancel move only the request list and AVAILABILITY (the freeze/release), so the
 *   account and fund reads go with them — a frozen balance is a different number to spend against.
 * - approve posts two balanced entries in two branches, so it moves everything a ledger fact moves.
 *
 * Taking the posted set for all of them is deliberate: the alternative is a caller that guesses which
 * transition happened, and a wrong guess leaves real money stale on screen.
 */
export function invalidateTransferRequestDependents(queryClient: FinanceQueryClient) {
  invalidateFinanceKeys(queryClient, [
    financeQueryKeys.transferRequests(),
    financeQueryKeys.accounts(),
    financeQueryKeys.funds(),
    financeQueryKeys.balances(),
    financeQueryKeys.transactions(),
    financeQueryKeys.overview(),
    financeQueryKeys.accounting(),
    financeQueryKeys.audit(),
    financeQueryKeys.investigation(),
    BUDGETS_ROOT_KEY,
  ])
}

function useFinanceTransferRequestsQuery(query: FinanceTransferRequestListQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.transferRequestList(query),
    queryFn: () => financeApi.listFinanceTransferRequests(query as FinanceTransferRequestListQuery),
    // `branchId` (either side) counts as a scope too. Omitting it here left the All tab permanently
    // disabled: the query never ran, so the table rendered its empty state for a branch with four transfers.
    enabled: Boolean(
      query?.workspaceId && (query?.branchId || query?.sourceBranchId || query?.destinationBranchId),
    ),
  })
}

/**
 * The contexts the given branch may send money to, parish included.
 *
 * Scoped by `sourceBranchId` in the key on purpose: the answer is per-sender, and a shared key would serve one
 * branch's destination list to another after a branch switch.
 */
function useFinanceTransferDestinationsQuery(workspaceId: string | null, sourceBranchId: string | null) {
  return useQuery({
    queryKey: financeQueryKeys.transferDestinations(workspaceId, sourceBranchId),
    queryFn: () =>
      financeApi.listFinanceTransferDestinations({
        workspaceId: workspaceId as string,
        sourceBranchId: sourceBranchId as string,
      }),
    enabled: Boolean(workspaceId && sourceBranchId),
  })
}

function useFinanceTransferRequestQuery(id: string | null) {
  return useQuery({
    queryKey: financeQueryKeys.transferRequestDetail(id),
    queryFn: () => financeApi.getFinanceTransferRequest(id as string),
    enabled: Boolean(id),
  })
}

/** Edit a DRAFT. Refused by the backend for every other status — a submitted request is immutable. */
function useUpdateFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Parameters<typeof financeApi.updateFinanceTransferRequest>[0]) =>
      financeApi.updateFinanceTransferRequest(input),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

/**
 * The transfer amount calculator. A MUTATION rather than a query: it is user-initiated ("calculate this now"),
 * takes a criteria object, and must not be cached — the ledger moves underneath it, and a stale total is a
 * wrong remittance. It writes nothing.
 */
/* --- Saved calculations: named, reusable remittance rules --- */

function useSavedTransferCalculationsQuery(query: { workspaceId?: string; branchId?: string } | null) {
  const enabled = Boolean(query?.workspaceId && query?.branchId)
  return useQuery({
    queryKey: financeQueryKeys.savedTransferCalculations(query?.workspaceId ?? "", query?.branchId ?? ""),
    queryFn: () =>
      financeApi.listSavedTransferCalculations({
        workspaceId: query!.workspaceId!,
        branchId: query!.branchId!,
      }),
    enabled,
  })
}

function useCreateSavedTransferCalculationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateSavedTransferCalculationInput) =>
      financeApi.createSavedTransferCalculation(input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: financeQueryKeys.savedTransferCalculations(input.workspaceId, input.branchId),
      })
    },
  })
}

function useDeleteSavedTransferCalculationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; workspaceId: string; branchId: string }) =>
      financeApi.deleteSavedTransferCalculation(input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: financeQueryKeys.savedTransferCalculations(input.workspaceId, input.branchId),
      })
    },
  })
}

function useDeriveTransferAmountMutation() {
  return useMutation({
    mutationFn: (query: DeriveTransferAmountQuery) => financeApi.deriveTransferAmount(query),
  })
}

function useCreateFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceTransferRequestInput) => financeApi.createFinanceTransferRequest(input),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useSubmitFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financeApi.submitFinanceTransferRequest(id),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useApproveFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ApproveFinanceTransferRequestInput) => financeApi.approveFinanceTransferRequest(input),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useRejectFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; rejectionReason?: string | null }) =>
      financeApi.rejectFinanceTransferRequest(input),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useReverseFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reversalReason?: string | null }) =>
      financeApi.reverseFinanceTransferRequest(input),
    // A reversal POSTS compensating entries, so it moves everything a ledger fact moves.
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useCancelFinanceTransferRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; cancellationReason?: string | null }) =>
      financeApi.cancelFinanceTransferRequest(input),
    onSuccess: () => invalidateTransferRequestDependents(queryClient),
  })
}

function useFinanceAccountsQuery(query: ListFinanceAccountsQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountList(query),
    queryFn: () => financeApi.listFinanceAccounts(query as ListFinanceAccountsQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

/** The supported "held as" account types, from the backend's live enum — so the picker follows the API. */
function useFinanceAccountTypesQuery() {
  return useQuery({
    queryKey: financeQueryKeys.accountTypes(),
    queryFn: () => financeApi.fetchFinanceAccountTypes(),
    staleTime: Infinity, // static enum metadata — never goes stale within a session
  })
}

function useFinanceFundsQuery(query: ListFinanceFundsQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.fundList(query),
    queryFn: () => financeApi.listFinanceFunds(query as ListFinanceFundsQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

/* --- Accounting reports (ADR-0021 tasks 20–22) --- */
function useAccountingChartQuery(query: AccountingChartQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountingChart(query),
    queryFn: () => financeApi.getAccountingChart(query as AccountingChartQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

function useAccountingJournalQuery(query: AccountingJournalQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountingJournal(query),
    queryFn: () => financeApi.getAccountingJournal(query as AccountingJournalQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

/**
 * Journal entries as an INFINITE query — the on-screen viewer scrolls through the whole journal one page at a
 * time (the plain `useAccountingJournalQuery` above stays for single-page/legacy callers). `query` omits `page`;
 * the page number is the pageParam. `getNextPageParam` stops once we have loaded `total` entries.
 */
function useAccountingJournalInfiniteQuery(query: AccountingJournalQuery | null) {
  return useInfiniteQuery({
    queryKey: financeQueryKeys.accountingJournalInfinite(query),
    queryFn: ({ pageParam }) =>
      financeApi.getAccountingJournal({ ...(query as AccountingJournalQuery), page: pageParam }),
    enabled: Boolean(query?.workspaceId),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.page - 1) * lastPage.limit + lastPage.items.length
      return loaded < lastPage.total ? lastPage.page + 1 : undefined
    },
  })
}

function useAccountingLedgerQuery(query: AccountingLedgerQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountingLedger(query),
    queryFn: () => financeApi.getAccountingLedger(query as AccountingLedgerQuery),
    enabled: Boolean(query?.workspaceId && query?.accountingAccountId),
  })
}

function useAccountingTrialBalanceQuery(query: AccountingPeriodQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountingTrialBalance(query),
    queryFn: () => financeApi.getAccountingTrialBalance(query as AccountingPeriodQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

function useAccountingStatementsQuery(query: AccountingPeriodQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.accountingStatements(query),
    queryFn: () => financeApi.getAccountingStatements(query as AccountingPeriodQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

/**
 * Financial health (IBA-117): the ratio set + the verdict on each, computed on the server from the ledger.
 * Scoped by the query object (workspace + branch/parish scope + period), never by a broad key.
 */
function useFinanceHealthQuery(query: AccountingPeriodQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.health(query),
    queryFn: () => {
      if (!query) throw new Error("Finance health requires a workspace scope")
      return financeApi.getFinanceHealth(query)
    },
    enabled: Boolean(query?.workspaceId),
  })
}

/** Active branch ACTIVE accounts list (bounded page); common default for picks and KPI labels. */
function useFinanceActiveBranchAccountsItemsQuery() {
  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const activeBranchSlug = useActiveBranchSlug()
  const { accessibleBranches, primaryBranch } = useUserBranchAccess()
  const activeBranch =
    accessibleBranches.find((branch) => branch.slug === activeBranchSlug) ?? primaryBranch ?? null

  const listQuery = React.useMemo((): ListFinanceAccountsQuery | null => {
    if (!workspaceId || !activeBranch?.id) return null
    return {
      workspaceId,
      branchId: activeBranch.id,
      scope: FinanceCategoryScope.Branch,
      status: FinanceDirectoryStatusFilter.Active,
      page: 1,
      limit: 100,
      sortBy: "name",
      sortOrder: "asc",
    }
  }, [workspaceId, activeBranch?.id])

  const base = useFinanceAccountsQuery(listQuery)
  const items: FinanceAccountRecord[] = base.data?.items ?? []

  return {
    ...base,
    data: items,
  }
}

function useFinanceCreateAccountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceAccountInput & { optimisticCurrency?: Currency }) =>
      financeApi.createFinanceAccount(input),
    onMutate: async (input) => {
      const id = optimisticId()
      const now = new Date().toISOString()
      const record: FinanceAccountRecord = {
        id,
        workspaceId: input.workspaceId,
        branchId: input.branchId ?? "",
        name: input.name,
        description: input.description ?? null,
        accountNumberLast4: input.accountNumberLast4 ?? null,
        type: input.type ?? FinanceAccountType.Bank,
        currency: input.optimisticCurrency ?? Currency.USD,
        status: "ACTIVE",
        scope: input.scope ?? FinanceCategoryScope.Branch,
        branch: { id: input.branchId ?? "", workspaceId: input.workspaceId, name: "", slug: "", status: "ACTIVE" },
        balance: input.openingBalance ?? "0",
        openingBalance: input.openingBalance ?? null,
        openingBalanceLocked: false,
        accountingAccountId: null,
        accountingAccountCode: null,
        hasLedgerActivity: false,
        canEditFinancialDetails: true,
        createdAt: now,
        updatedAt: now,
      }
      const snapshots = await optimisticallyPatchLists<FinanceAccountRecord>(
        queryClient,
        financeQueryKeys.accounts(),
        (items, key) => {
          const query = key[3] as ListFinanceAccountsQuery | null | undefined
          const sameContext = query?.workspaceId === input.workspaceId &&
            (query.branchId ?? null) === (input.branchId ?? null) &&
            (query.scope ?? FinanceCategoryScope.Branch) === (input.scope ?? FinanceCategoryScope.Branch)
          const visibleByStatus = !query?.status || query.status === FinanceDirectoryStatusFilter.Active
          const visibleBySearch = !query?.search || input.name.toLowerCase().includes(query.search.toLowerCase())
          return sameContext && visibleByStatus && visibleBySearch ? [record, ...items] : items
        },
      )
      return { id, snapshots }
    },
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSuccess: (record, _input, context) => {
      if (context) replaceOptimisticRow(queryClient, financeQueryKeys.accounts(), context.id, record)
    },
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

/** Create a first-class income/expense account in the Chart of Accounts (categories→accounts convergence). */
function useCreateAccountingAccountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAccountingAccountInput) => financeApi.createAccountingAccount(input),
    onMutate: async (input) => {
      const id = optimisticId()
      const isIncome = input.kind === "INCOME"
      const record: ChartAccount = {
        id,
        code: "…",
        name: input.name,
        translationKey: null,
        type: isIncome ? AccountingAccountType.Income : AccountingAccountType.Expense,
        classification: isIncome
          ? AccountingAccountClassification.Revenue
          : AccountingAccountClassification.OperatingExpense,
        normalBalance: isIncome ? NormalBalance.Credit : NormalBalance.Debit,
        status: "ACTIVE",
        balance: "0",
      }
      const key = [...financeQueryKeys.accounting(), "chart"] as const
      const snapshots = await optimisticallyPatchLists<ChartAccount>(queryClient, key, (items, cacheKey) => {
        const query = cacheKey[3] as AccountingChartQuery | null | undefined
        const sameWorkspace = query?.workspaceId === input.workspaceId
        const sameOwner = input.owningBranchId ? query?.branchId === input.owningBranchId : true
        const sameType = !query?.type || query.type === record.type
        return sameWorkspace && sameOwner && sameType ? [record, ...items] : items
      })
      return { id, snapshots, key }
    },
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceUpdateAccountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateFinanceAccountInput) => financeApi.updateFinanceAccount(input),
    onMutate: async (input) => ({
      snapshots: await optimisticallyPatchLists<FinanceAccountRecord>(queryClient, financeQueryKeys.accounts(), (items) =>
        items.map((item) => item.id === input.id ? { ...item, ...input.patch, updatedAt: new Date().toISOString() } : item),
      ),
    }),
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceDeactivateAccountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financeApi.deactivateFinanceAccount(id),
    onMutate: async (id) => ({
      snapshots: await optimisticallyPatchLists<FinanceAccountRecord>(queryClient, financeQueryKeys.accounts(), (items) =>
        items.map((item) => item.id === id ? { ...item, status: "ARCHIVED" } : item),
      ),
    }),
    onError: (_error, _id, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceActivateAccountMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => financeApi.activateFinanceAccount(id),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceCreateFundMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceFundInput) => financeApi.createFinanceFund(input),
    onMutate: async (input) => {
      const id = optimisticId()
      const now = new Date().toISOString()
      const record: FinanceFundRecord = {
        id, workspaceId: input.workspaceId, branchId: input.branchId ?? "", name: input.name,
        nameTranslationKey: null, description: input.description ?? null, status: FinanceFundStatus.Active,
        kind: FinanceFundKind.Custom, linkedIncomeCategoryId: null, linkedIncomeAccountId: null,
        scope: input.scope ?? FinanceCategoryScope.Branch, targetAmount: input.targetAmount ?? null,
        startDate: input.startDate ?? null, endDate: input.endDate ?? null, isRestricted: input.isRestricted ?? false,
        restrictionStartDate: input.restrictionStartDate ?? null, restrictionEndDate: input.restrictionEndDate ?? null,
        allowedExpenseAccountIds: input.allowedExpenseAccountIds ?? [], currency: null, balance: "0", reserved: "0",
        available: "0", activityCount: 0, dedicatedActivityId: input.dedicateToActivityId ?? null,
        dedicatedActivityName: null,
        branch: { id: input.branchId ?? "", workspaceId: input.workspaceId, name: "", slug: "", status: "ACTIVE" },
        createdAt: now, updatedAt: now,
      }
      const snapshots = await optimisticallyPatchLists<FinanceFundRecord>(queryClient, financeQueryKeys.funds(), (items, key) => {
        const query = key[3] as ListFinanceFundsQuery | null | undefined
        const sameContext = query?.workspaceId === input.workspaceId &&
          (query.branchId ?? null) === (input.branchId ?? null) &&
          (query.scope ?? FinanceCategoryScope.Branch) === (input.scope ?? FinanceCategoryScope.Branch)
        const visibleByStatus = !query?.status || query.status === "ACTIVE"
        const visibleBySearch = !query?.search || input.name.toLowerCase().includes(query.search.toLowerCase())
        return sameContext && visibleByStatus && visibleBySearch ? [record, ...items] : items
      })
      return { id, snapshots }
    },
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSuccess: (record, _input, context) => {
      if (context) replaceOptimisticRow(queryClient, financeQueryKeys.funds(), context.id, record)
    },
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceCreateSetupFundMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceSetupFundInput) => financeApi.createFinanceSetupFund(input),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function useReallocateFundsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ReallocateFundsInput) => financeApi.reallocateFunds(input),
    // A reallocation changes TWO funds' balances, and fund balances are rendered well beyond the funds list —
    // the fund-watchlist overview widget, fund utilization, and fund activity all read them. Invalidating only
    // ["finance","funds"] left those showing pre-reallocation numbers until an unrelated refetch, so this takes
    // the whole directory set (ADR-0007: invalidate what the write actually changed).
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceUpdateFundMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateFinanceFundInput) => financeApi.updateFinanceFund(input),
    onMutate: async (input) => ({
      snapshots: await optimisticallyPatchLists<FinanceFundRecord>(queryClient, financeQueryKeys.funds(), (items) =>
        items.map((item) => {
          if (item.id !== input.id) return item
          const { name, ...patch } = input.patch
          return {
            ...item,
            ...patch,
            ...(name === undefined ? {} : { name: name }),
            updatedAt: new Date().toISOString(),
          }
        }),
      ),
    }),
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceSetFundStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; status: FinanceFundRecord["status"] }) =>
      financeApi.setFinanceFundStatus(input),
    onMutate: async (input) => ({
      snapshots: await optimisticallyPatchLists<FinanceFundRecord>(queryClient, financeQueryKeys.funds(), (items) =>
        items.map((item) => item.id === input.id ? { ...item, status: input.status } : item),
      ),
    }),
    onError: (_error, _input, context) => rollbackOptimisticLists(queryClient, context?.snapshots),
    onSettled: () => invalidateFinanceDirectory(queryClient),
  })
}

/**
 * Dedicate a fund to one activity, or release it (IBA-175).
 *
 * Takes the whole directory set, not just the funds list: a dedication changes which fund a transaction
 * form offers for an activity, so the activity views are stale too.
 */
function useFinanceSetFundDedicationMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; activityId: string | null }) => financeApi.setFinanceFundDedication(input),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceAttachFundActivitiesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; activityIds: string[] }) =>
      financeApi.attachFinanceFundActivities(input),
    // Attaching activities to a fund moves both the funds list and the activities list — the directory set.
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

/* --- IBA-67 income fund-allocation rules (per income category) --- */






function useFinanceTransactionRecordsQuery(query: FinanceTransactionListQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.transactionList(query),
    queryFn: () => financeApi.listFinanceTransactionRecords(query as FinanceTransactionListQuery),
    // Transaction reads are branch-scoped by contract. Do not let route/bootstrap timing call the API with only
    // a workspace id, because the server deliberately refuses that ambiguous request under branch autonomy.
    enabled: Boolean(query?.workspaceId && query?.branchId),
  })
}

function useFinanceTransactionRecordPageQuery(query: FinanceTransactionListQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.transactionPage(query),
    queryFn: () => financeApi.listFinanceTransactionRecordPage(query as FinanceTransactionListQuery),
    enabled: Boolean(query?.workspaceId && query?.branchId),
  })
}

function useReceiptConfigQuery(input: { workspaceId: string; branchId: string } | null) {
  return useQuery({
    queryKey: financeQueryKeys.receiptConfig(input),
    queryFn: () => financeApi.getReceiptConfig(input as { workspaceId: string; branchId: string }),
    enabled: Boolean(input?.workspaceId && input?.branchId),
  })
}

function useUpdateReceiptConfigMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { workspaceId: string; branchId: string; config: ReceiptConfig }) =>
      financeApi.updateReceiptConfig(input),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function usePreviewReceiptConfigMutation() {
  return useMutation({
    mutationFn: (input: { workspaceId: string; branchId: string; config: ReceiptConfig }) =>
      financeApi.previewReceiptConfig(input),
  })
}

function useFinanceActivitiesQuery(query: FinanceActivitiesListQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.activityList(query),
    queryFn: () => financeApi.listFinanceActivities(query as FinanceActivitiesListQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

function useFinanceOverviewQuery(query: FinanceOverviewQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.overviewSummary(query),
    queryFn: () => financeApi.getFinanceOverview(query as FinanceOverviewQuery),
    enabled: Boolean(query?.workspaceId),
    /*
     * Branch autonomy (ADR-0024) sends every money figure as a SHARE of its group's total rather than an
     * amount, and says so on `meta.figuresAreRatios`. Swapping the currency for the ratio sentinel HERE — in
     * the one hook every finance widget already shares — makes all twenty-odd of them format percentages
     * without a single one having to know what governance is.
     *
     * The alternative was threading a flag through every widget, where the ones that forgot would print a
     * share with a dollar sign in front of it. That is the same class of mistake as the zeroes this replaced.
     */
    select: (data) =>
      data?.meta?.figuresAreRatios
        ? { ...data, meta: { ...data.meta, currency: RATIO_DISPLAY_CURRENCY } }
        : data,
  })
}

function useFinanceParishTransactionsQuery(query: ListParishTransactionsQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.parishTransactionList(query),
    queryFn: () => financeApi.listFinanceParishTransactions(query as ListParishTransactionsQuery),
    enabled: Boolean(query?.workspaceId),
  })
}

function useFinanceCurrentIncomeBalanceQuery(query: FinanceCurrentIncomeBalanceQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.currentIncomeBalance(query),
    queryFn: () => financeApi.getFinanceCurrentIncomeBalance(query as FinanceCurrentIncomeBalanceQuery),
    enabled: Boolean(query?.workspaceId && query?.branchId && query?.incomeCategoryId),
  })
}

function useFinanceCurrentIncomeBalancesQuery(query: FinanceCurrentIncomeBalancesQuery | null) {
  return useQuery({
    queryKey: financeQueryKeys.currentIncomeBalances(query),
    queryFn: () => financeApi.getFinanceCurrentIncomeBalances(query as FinanceCurrentIncomeBalancesQuery),
    enabled: Boolean(query?.workspaceId && query?.branchId),
  })
}

function useFinanceCreateParishIncomeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateParishIncomeRequest) => financeApi.createFinanceParishIncome(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

function useFinanceCreateParishExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateParishExpenseRequest) => financeApi.createFinanceParishExpense(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

/**
 * IBA-69 budget-line match preview. Scoped key (never a broad finance key) so a change to one
 * transaction draft cannot invalidate another. Enabled only once the minimally-required fields are
 * known. No placeholder data is retained: a branch/input switch must never paint the previous preview.
 */
function useBudgetLineMatchesQuery(query: BudgetLineMatchQuery | null) {
  // categories→accounts: the draft needs an income/expense account before a match preview is meaningful.
  const hasAccount = Boolean(query?.accountingAccountId)
  return useQuery({
    queryKey: financeQueryKeys.budgetLineMatches(query),
    queryFn: () => financeApi.fetchBudgetLineMatches(query as BudgetLineMatchQuery),
    enabled: Boolean(query?.workspaceId && query?.branchId && query?.type && query?.amount && query?.occurredAt) && hasAccount,
    staleTime: 0,
    retry: false,
  })
}

function useFinanceCreateTransactionRecordMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceTransactionInput) => financeApi.createTransactionRecord(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

/**
 * Create an internal account-to-account transfer. A transfer changes two account balances, so it invalidates
 * the SAME dependents as any ledger transaction write (both accounts, the balances, the accounting reports,
 * the overview, and budgets).
 */
function useCreateTransferMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTransferInput) => financeApi.createTransfer(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

/**
 * Edit a posted transaction's metadata (description / payment method / reference).
 *
 * Invalidated exactly like a cancel: the transaction lists, its detail and the reconciliation lines all render
 * these fields, and a reference corrected in one place must not still read the old way in another.
 */
function useFinanceUpdateTransactionMetadataMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateFinanceTransactionMetadataInput) =>
      financeApi.updateFinanceTransactionMetadata(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

function useFinanceCancelTransactionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CancelFinanceTransactionInput) => financeApi.cancelFinanceTransaction(input),
    onSuccess: () => invalidateTransactionDependents(queryClient),
  })
}

function useFinanceCreateActivityMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFinanceActivityInput) => financeApi.createFinanceActivity(input),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

function useFinanceUpdateActivityMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateFinanceActivityInput) => financeApi.updateFinanceActivity(input),
    onSuccess: () => invalidateFinanceDirectory(queryClient),
  })
}

export function useFinanceFeatureQuery() {
  return {
    useFinanceAccountsQuery,
    useFinanceFundsQuery,
    useFinanceCreateFundMutation,
    useFinanceCreateSetupFundMutation,
    useReallocateFundsMutation,
    useFinanceUpdateFundMutation,
    useFinanceSetFundStatusMutation,
    useFinanceAttachFundActivitiesMutation,
    useFinanceSetFundDedicationMutation,
    useFinanceActiveBranchAccountsItemsQuery,
    useFinanceCreateAccountMutation,
    useFinanceUpdateAccountMutation,
    useFinanceDeactivateAccountMutation,
    useFinanceActivateAccountMutation,
    useFinanceTransactionRecordsQuery,
    useFinanceTransactionRecordPageQuery,
    useFinanceActivitiesQuery,
    useFinanceOverviewQuery,
    useFinanceParishTransactionsQuery,
    useFinanceCurrentIncomeBalanceQuery,
    useFinanceCreateParishIncomeMutation,
    useFinanceCreateParishExpenseMutation,
    useFinanceCreateTransactionRecordMutation,
    useCreateTransferMutation,
    useBudgetLineMatchesQuery,
    useFinanceCancelTransactionMutation,
    useFinanceUpdateTransactionMetadataMutation,
    useFinanceCreateActivityMutation,
    useFinanceUpdateActivityMutation,
  }
}

export {
  useFinanceTransferDestinationsQuery,
  useFinanceTransferRequestQuery,
  useFinanceTransferRequestsQuery,
  useCreateFinanceTransferRequestMutation,
  useDeriveTransferAmountMutation,
  useSavedTransferCalculationsQuery,
  useCreateSavedTransferCalculationMutation,
  useDeleteSavedTransferCalculationMutation,
  useSubmitFinanceTransferRequestMutation,
  useUpdateFinanceTransferRequestMutation,
  useApproveFinanceTransferRequestMutation,
  useRejectFinanceTransferRequestMutation,
  useCancelFinanceTransferRequestMutation,
  useReverseFinanceTransferRequestMutation,
  useFinanceAccountsQuery,
  useFinanceAccountTypesQuery,
  useReallocateFundsMutation,
  useFinanceFundsQuery,
  useAccountingChartQuery,
  useCreateAccountingAccountMutation,
  useAccountingJournalQuery,
  useAccountingJournalInfiniteQuery,
  useAccountingLedgerQuery,
  useAccountingTrialBalanceQuery,
  useAccountingStatementsQuery,
  useFinanceHealthQuery,
  useFinanceCreateFundMutation,
  useFinanceCreateSetupFundMutation,
  useFinanceUpdateFundMutation,
  useFinanceSetFundStatusMutation,
  useFinanceSetFundDedicationMutation,
  useFinanceAttachFundActivitiesMutation,
  useFinanceActiveBranchAccountsItemsQuery,
  useFinanceCreateAccountMutation,
  useFinanceUpdateAccountMutation,
  useFinanceDeactivateAccountMutation,
  useFinanceActivateAccountMutation,
  useFinanceTransactionRecordsQuery,
  useFinanceTransactionRecordPageQuery,
  useReceiptConfigQuery,
  useUpdateReceiptConfigMutation,
  usePreviewReceiptConfigMutation,
  useFinanceActivitiesQuery,
  useFinanceOverviewQuery,
  useFinanceParishTransactionsQuery,
  useFinanceCurrentIncomeBalanceQuery,
  useFinanceCurrentIncomeBalancesQuery,
  useFinanceCreateParishIncomeMutation,
  useFinanceCreateParishExpenseMutation,
  useFinanceCreateTransactionRecordMutation,
  useCreateTransferMutation,
  useBudgetLineMatchesQuery,
  useFinanceCancelTransactionMutation,
  useFinanceUpdateTransactionMetadataMutation,
  useFinanceCreateActivityMutation,
  useFinanceUpdateActivityMutation,
}
